import {
  Icon,
  PageContainer,
  RouteIconButtonLink,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiEye } from "@mdi/js";
import {
  Alert,
  Box,
  Paper,
  Skeleton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tooltip,
} from "@mui/material";
import { createFileRoute, stripSearchParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useListCreatePurchaseOrderBySpots } from "../../../../__generated__/orval/endpoints/create-purchase-order-by-spots/create-purchase-order-by-spots";
import { EntitiesCreatePurchaseOrderBySpotCollectionListResponse } from "../../../../__generated__/orval/models/entitiesCreatePurchaseOrderBySpotCollectionListResponse.zod";
import { ForbiddenError } from "../../../../errors/ForbiddenError";
import { i18nextParseToDate } from "../../../../utils/i18nextParseTo";

export const executionStatuses = ["processing", "completed", "failed"] as const;
type ExecutionStatus = (typeof executionStatuses)[number];

const executionSearchSchema = z.object({
  itemsPerPage: z.number().int().min(1).max(100).catch(25).default(25),
  page: z.number().int().min(1).catch(1).default(1),
  status: z.enum(executionStatuses).catch("processing").default("processing"),
});

const ExecutionList = () => {
  const { t } = useTranslation();
  const { itemsPerPage, page, status } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data, isPending, isError } = useListCreatePurchaseOrderBySpots({
    execution_status: status,
    items_per_page: itemsPerPage,
    order: "desc",
    page,
  });
  const parsed =
    EntitiesCreatePurchaseOrderBySpotCollectionListResponse.safeParse(data);

  return (
    <PageContainer
      showBackButton
      title={t("spotPurchaseOrder.executionsTitle")}
    >
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          onChange={(_event, value: ExecutionStatus) => {
            navigate({
              search: (previous) => ({ ...previous, page: 1, status: value }),
            });
          }}
          value={status}
        >
          {executionStatuses.map((value) => (
            <Tab
              key={value}
              label={t(`spotPurchaseOrder.tabs.${value}`)}
              value={value}
            />
          ))}
        </Tabs>
      </Box>
      {isError || (!isPending && !parsed.success) ? (
        <Alert severity="error">{t("spotPurchaseOrder.loadError")}</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell>
                  {t("createPurchaseOrderByQuotation.createdAt")}
                </TableCell>
                <TableCell>
                  {t("createPurchaseOrderByQuotation.updatedAt")}
                </TableCell>
                <TableCell>{t("spotPurchaseOrder.fileName")}</TableCell>
                <TableCell>{t("spotPurchaseOrder.uploadedBy")}</TableCell>
                <TableCell>{t("common.rfqType")}</TableCell>
                <TableCell align="center">
                  {t("spotPurchaseOrder.itemsCount")}
                </TableCell>
                <TableCell align="center" />
              </TableRow>
            </TableHead>
            <TableBody>
              {isPending ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Skeleton />
                  </TableCell>
                </TableRow>
              ) : parsed.success && parsed.data.results.length ? (
                parsed.data.results.map((batch) => (
                  <TableRow key={batch.id}>
                    <TableCell>
                      {t("intlDateTime", i18nextParseToDate(batch.created_at))}
                    </TableCell>
                    <TableCell>
                      {t("intlDateTime", i18nextParseToDate(batch.updated_at))}
                    </TableCell>
                    <TableCell>{batch.file_name}</TableCell>
                    <TableCell>{batch.create_purchase_order_by}</TableCell>
                    <TableCell>{batch.rfq_type}</TableCell>
                    <TableCell align="center">{batch.rows_count}</TableCell>
                    <TableCell align="center">
                      <Tooltip title={t("view")}>
                        <RouteIconButtonLink
                          aria-label={t("view")}
                          params={{ createPurchaseOrderBySpotId: batch.id }}
                          search={{ itemsPerPage, page }}
                          to="/create_purchase_order_by_spots/$createPurchaseOrderBySpotId"
                        >
                          <Icon icon={mdiEye} />
                        </RouteIconButtonLink>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7}>
                    {t("spotPurchaseOrder.noItems")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  count={parsed.success ? parsed.data.total_items : 0}
                  onPageChange={(_event, value) =>
                    navigate({
                      search: (previous) => ({ ...previous, page: value + 1 }),
                    })
                  }
                  onRowsPerPageChange={(event) => {
                    navigate({
                      search: (previous) => ({
                        ...previous,
                        itemsPerPage: Number(event.target.value),
                        page: 1,
                      }),
                    });
                  }}
                  page={page - 1}
                  rowsPerPage={itemsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  showFirstButton
                  showLastButton
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      )}
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/create_purchase_order_by_spots/executions/",
  // biome-ignore assist/source/useSortedKeys: ordering required for type inference, waiting a TanStack Biome plugin
)({
  validateSearch: executionSearchSchema,
  search: {
    middlewares: [stripSearchParams({ itemsPerPage: 25, page: 1 })],
  },
  beforeLoad({ context }) {
    if (context.auth.user?.roles_names.includes("Spot - View")) {
      return;
    }

    throw new ForbiddenError();
  },
  component: ExecutionList,
});
